/**
 * Cột & format cho datasheet Cash-in — bám `frmFuturesQryBankCashIn` (form Access).
 * Thứ tự + format khớp control Top của TextBox: Date → Bank → Cash-in → CCY-in → Cash-out → CCY-out →
 *   FX → Cash-in (USD) → Cash-out (USD) → Updated Date → Updated Person.
 */
export type BankCashColumnKind = 'text' | 'date' | 'datetime' | 'integer' | 'fx' | 'usd'

export interface BankCashColumnDef {
  key: string
  label: string
  kind?: BankCashColumnKind
  align?: 'right'
}

export const BANK_CASH_COLUMNS: BankCashColumnDef[] = [
  { key: 'date', label: 'Date', kind: 'date' },
  { key: 'bank', label: 'Bank' },
  { key: 'cashIn', label: 'Cash-in', kind: 'integer', align: 'right' },
  { key: 'currencyIn', label: 'Cash-in Currency' },
  { key: 'cashOut', label: 'Cash-out', kind: 'integer', align: 'right' },
  { key: 'currencyOut', label: 'Cash-out Currency' },
  { key: 'fx', label: 'FX', kind: 'fx', align: 'right' },
  { key: 'cashInUsd', label: 'Cash-in (USD)', kind: 'usd', align: 'right' },
  { key: 'cashOutUsd', label: 'Cash-out (USD)', kind: 'usd', align: 'right' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'datetime' },
  { key: 'personUpdate', label: 'Updated Person' },
]

/** Cột có thể cộng tổng ở footer (Access không hiện total nhưng FE giữ tổng dòng tiền). */
export const BANK_CASH_TOTAL_COLUMNS = new Set<string>(['cashIn', 'cashOut', 'cashInUsd', 'cashOutUsd'])

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatInteger = (n: number): string => {
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  return n < 0 ? `(${abs})` : abs
}

const formatTwoDp = (n: number): string => {
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return n < 0 ? `(${abs})` : abs
}

const formatUsd = (n: number): string => {
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return n < 0 ? `$ (${abs})` : `$ ${abs}`
}

export function formatBankCashCell(value: unknown, col: BankCashColumnDef): string {
  if (value == null || value === '') return ''
  switch (col.kind) {
    case 'date':
      return formatDate(value)
    case 'datetime':
      return formatDateTime(value)
    case 'integer':
      return typeof value === 'number' ? formatInteger(value) : String(value)
    case 'fx':
      return typeof value === 'number' ? formatTwoDp(value) : String(value)
    case 'usd':
      return typeof value === 'number' ? formatUsd(value) : String(value)
    default:
      return String(value)
  }
}

export function formatBankCashTotal(value: number, col: BankCashColumnDef): string {
  if (col.kind === 'usd') return formatUsd(value)
  return formatInteger(value)
}
